import { useEffect, useState } from "react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section, SectionHeader } from "../components/Section";
import { Card } from "../components/Card";
import { CTASection } from "../components/CTASection";
import { getGroups, COMMUNITY_GROUPS, type Group } from "../lib/wix/groups";
import { useTranslation } from "../lib/i18n";

const CATEGORY_KEY: Record<string, string> = {
  domain: "community.cat.domain",
  role: "community.cat.role",
  cohort: "community.cat.cohort",
};

export default function CommunityPage() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getGroups().then((g) => {
      setGroups(g.length > 0 ? g : COMMUNITY_GROUPS);
      setLoading(false);
    });
  }, []);

  return (
    <>
      <SEO
        title="Community — Sentinel"
        description="Sentinel community powered by Wix Groups. Domain communities for operators, engineers, OEM partners, and safety professionals."
        canonical="/community"
        image="https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=1200"
      />
      <PageHero
        eyebrow={t("community.page.eyebrow")}
        title={t("community.page.title")}
        description={t("community.page.description")}
        bgVariant="primary"
        bgImage="https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      <Section>
        <SectionHeader
          eyebrow={t("community.list.eyebrow")}
          title={t("community.list.title")}
        />
        {loading ? (
          <p className="text-center text-muted">{t("common.loadingCommunities")}</p>
        ) : (
          <div className="grid grid-3">
            {groups.map((group) => (
              <Card key={group.id} hover>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.75rem" }}>
                  <span className="badge badge-accent">{CATEGORY_KEY[group.category] ? t(CATEGORY_KEY[group.category]) : group.category}</span>
                  <span className="text-muted" style={{ fontSize: "0.8125rem" }}>
                    {group.memberCount.toLocaleString()} {t("common.members")}
                  </span>
                </div>
                <h3 style={{ fontSize: "1.0625rem", marginBottom: "0.5rem" }}>{group.name}</h3>
                <p className="text-muted" style={{ fontSize: "0.9375rem" }}>{group.description}</p>
              </Card>
            ))}
          </div>
        )}
      </Section>

      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("community.inside.eyebrow")}
          title={t("community.inside.title")}
        />
        <div className="grid grid-4">
          {[
            { t: t("community.item1.title"), d: t("community.item1.desc") },
            { t: t("community.item2.title"), d: t("community.item2.desc") },
            { t: t("community.item3.title"), d: t("community.item3.desc") },
            { t: t("community.item4.title"), d: t("community.item4.desc") },
            { t: t("community.item5.title"), d: t("community.item5.desc") },
            { t: t("community.item6.title"), d: t("community.item6.desc") },
            { t: t("community.item7.title"), d: t("community.item7.desc") },
            { t: t("community.item8.title"), d: t("community.item8.desc") },
          ].map((item) => (
            <Card key={item.t}>
              <h3 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>{item.t}</h3>
              <p className="text-muted" style={{ fontSize: "0.875rem" }}>{item.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <CTASection
        title={t("community.cta.title")}
        description={t("community.cta.description")}
        primaryLabel={t("cta.registerNow")}
        primaryTo="/register"
        secondaryLabel={t("cta.contactUs")}
        secondaryTo="/contact"
      />
    </>
  );
}
